import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const THEME_KEY = "theme"; // 'light' | 'dark' | 'system'

function getSystemPrefersDark() {
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function applyTheme(mode) {
  const root = document.documentElement;
  const isDark =
    mode === "dark" || (mode === "system" && getSystemPrefersDark());
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
}

export function useTheme() {
  const [mode, setMode] = useState(() => {
    // initial read (SSR-safe: guarded by pre-init script anyway)
    try {
      return sessionStorage.getItem(THEME_KEY) || "system";
    } catch {
      return "system";
    }
  });

  // keep system listener only when mode === 'system'
  const mediaRef = useRef(null);

  useEffect(() => {
    applyTheme(mode);

    try {
      sessionStorage.setItem(THEME_KEY, mode);
    } catch {}

    if (mode === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      mediaRef.current = mq;
      const handler = () => applyTheme("system");
      mq.addEventListener
        ? mq.addEventListener("change", handler)
        : mq.addListener(handler);
      return () => {
        mq.removeEventListener
          ? mq.removeEventListener("change", handler)
          : mq.removeListener(handler);
      };
    }
  }, [mode]);

  const isDark = useMemo(() => {
    return mode === "dark" || (mode === "system" && getSystemPrefersDark());
  }, [mode]);

  const setTheme = useCallback((next) => setMode(next), []);
  const toggle = useCallback(
    () => setMode((m) => (m === "dark" ? "light" : "dark")),
    []
  );

  return { mode, isDark, setTheme, toggle };
}
